import React, { useEffect, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { push } from '@lagunovsky/redux-react-router'
import { isNull, keys, min, minBy } from 'lodash'
import moment from 'moment'
import { CardContent, Grid, Tooltip } from '@mui/material'
import { FormatListBulleted, MultilineChart } from '@mui/icons-material'
import validateImg from '../../../assets/pictures/pictos/validate.png'
import purgeImg from '../../../assets/pictures/pictos/purge.png'
import importImg from '../../../assets/pictures/pictos/import.png'
import compensationImg from '../../../assets/pictures/pictos/compensation.png'
import correctionImg from '../../../assets/pictures/pictos/correction.png'
import optionsImg from '../../../assets/pictures/pictos/options.png'
import thresholdImg from '../../../assets/pictures/pictos/threshold.png'
import eventImg from '../../../assets/pictures/pictos/event.png'
import Card from '../../../components/card/Card'
import MessageCard from '../../../components/card/MessageCard'
import ProgressCard from '../../../components/card/ProgressCard'
import Select from '../../../components/forms/Select'
import Checkbox from '../../../components/forms/Checkbox'
import SuperMultiAutocomplete from '../../../components/forms/SuperMultiAutocomplete'
import IconTool from '../../../components/icon/IconTool'
import MUIChartTabs from '../../../components/echart/MUIChartTabs'
import SimpleChartTabsPluvio from '../../../components/echart/SimpleChartTabsPluvio'
import { getChartDate, getSubstractTime } from '../../../components/echart/EChartUtils'
import { getLinks, getStationTitleWithTownName } from '../../../utils/StationUtils'
import { getStationArrowNav } from '../../../utils/ActionUtils'
import { getFullDateInRef } from '../../../utils/DateUtil'
import useMeasureTempRef from '../../../utils/customHook/useMeasureTempRef'
import ChartTempRefChip from '../../../components/echart/ChartTempRefChip'
import { hasValue } from '../../../utils/NumberUtil'
import { getLocalStorageJson } from '../../../utils/FormUtils'
import { componentHasHabilitations } from '../../../utils/HabilitationUtil'
import useTitle from '../../../utils/customHook/useTitle'
import useActions from '../../../utils/customHook/useActions'
import useUpdateEffect from '../../../utils/customHook/useUpdateEffect'
import useProgressDispatch from '../../../utils/customHook/useProgressDispatch'
import useLocalStorage from '../../../utils/customHook/useLocalStorage'
import useAccountSetting from '../../../utils/customHook/useAccountSetting'
import { STATION_TYPE_NAME } from '../../../station/constants/StationConstants'
import { H_STATION_PLUVIO_VALIDATION } from '../../../account/constants/AccessRulesConstants'
import { HISTO, J90 } from '../../../alerting/constants/ChartFollowContants'
import { CHART_SELECTED_TIME, CHART_SELECTED_TIME_OTHER, OTHER } from '../../../quality/constants/ChartConstant'
import { AUTO, PLUVIO_TEMP_REF } from '../../constants/PluvioOptionConstant'
import { getAutoCmumul, getGroupFunc } from '../../../utils/PluviometryUtils'
import PluviometryAction from '../../actions/PluviometryAction'
import EventsAction from '../../../events/actions/EventsAction'
import AdministrationAction from '../../../administration/actions/AdministrationAction'
import ContributorAction from '../../../referencial/components/contributor/actions/ContributorAction'
import ToastrAction from '../../../toastr/actions/ToastrAction'
import ThresholdsTab from '../../../alerting/components/dashboard/tabPopin/ThresholdsTab'
import SimpleOptionsToolPanel from '../../../piezometry/components/validation/tools/SimpleOptionsToolPanel'
import ValidationPluvioToolPanel from './tools/ValidationPluvioToolPanel'
import DeletePluvioToolPanel from './tools/DeletePluvioToolPanel'
import CompensationPluvioToolPanel from './tools/CompensationPluvioToolPanel'
import CorrectionPluvioToolPanel from './tools/CorrectionPluvioToolPanel'
import ImportPluvioToolPanel from './tools/ImportPluvioToolPanel'
import EventsToolPanelPluvio from './tools/EventsToolPanelPluvio'
import TableViewValidationPluvioMode from './displayMode/TableViewValidationPluvioMode'
import GraphicValidationPluvioMode from './displayMode/GraphicValidationPluvioMode'
import {
    COMPENSATION,
    CORRECTION,
    DELETE,
    DEFAULT_VALIDATION_DISPLAY_MODES,
    EVENT,
    IMPORT,
    OPTIONS,
    PLUVIO_VALIDATION_CHART_OPTIONS,
    PURGE,
    RESET,
    THRESHOLDS,
    VALIDATION,
    WINDOW,
} from './constants/PluvioValidationConstants'

const listView = [VALIDATION, PURGE, EVENT, THRESHOLDS]

const PluviometryValidationApp = () => {
    const {
        pluviometer,
        pluviometers,
        pluvioMeasures,
        pluviometryDataTypes,
        pluviometerStatistics,
        pluviometerThresholds,
        pluviometerLastMeasures,
        applicationSettings,
        contributors,
        citiesIndex,
    } = useSelector(store => ({
        pluviometer: store.PluviometryReducer.pluviometer,
        pluviometers: store.PluviometryReducer.pluviometers,
        pluvioMeasures: store.PluviometryReducer.pluvioMeasures,
        pluviometryDataTypes: store.PluviometryReducer.pluviometryDataTypes,
        pluviometerStatistics: store.PluviometryReducer.pluviometerStatistics,
        pluviometerThresholds: store.PluviometryReducer.pluviometerThresholds,
        pluviometerLastMeasures: store.PluviometryReducer.pluviometerLastMeasures,
        applicationSettings: store.AdministrationReducer.applicationSettings,
        contributors: store.ContributorReducer.contributors,
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const lastSelectedTime = useAccountSetting(CHART_SELECTED_TIME, v => [HISTO, OTHER].includes(v) ? v : (v ? parseInt(v) : J90))
    const lastSelectedTimeOther = getLocalStorageJson(CHART_SELECTED_TIME_OTHER)
    const getDefaultMinDate = () => {
        if (lastSelectedTime === OTHER) {
            return lastSelectedTimeOther?.minDate
        }
        return lastSelectedTime === HISTO ? undefined : getSubstractTime(lastSelectedTime)
    }
    const getDefaultMaxDate = () => {
        if (lastSelectedTime === OTHER) {
            return lastSelectedTimeOther?.maxDate
        }
        return lastSelectedTime === HISTO ? moment().valueOf() : undefined
    }

    const [time, setTime] = useState(lastSelectedTime || J90)
    const [dataType, setDataType] = useState(null)
    const [tempRef, setTempRef] = useLocalStorage(PLUVIO_TEMP_REF, undefined)
    const stationTempRefOffset = useMeasureTempRef(pluviometer?.link_tempRefs, dataType)
    const tempRefOffset = useMeasureTempRef(pluviometer?.link_tempRefs, dataType, tempRef)
    const [selectedCodePoint, setSelectedCodePoint] = useState(null)
    const [tableView, setTableView] = useState(false)
    const [tool, setTool] = useState(VALIDATION)
    const [selection, setSelection] = useState(WINDOW)
    const [selectionData, setSelectionData] = useState()
    const [dataLoaded, setDataLoaded] = useState(false)
    const [startDate, setStartDate] = useState(undefined)
    const [endDate, setEndDate] = useState(undefined)
    const [minDate, setMinDate] = useState(getDefaultMinDate())
    const [maxDate, setMaxDate] = useState(getDefaultMaxDate())
    const [value, setValue] = useState()
    const [initialPoint, setInitialPoint] = useState()
    const [isChange, setIsChange] = useState(false)
    const [isPreview, setIsPreview] = useState(false)
    const [isThresholShow, setIsThresholShow] = useState(true)
    const [serieLinePrevious, setSerieLinePrevious] = useState([])
    const [valueCorrection, setValueCorrection] = useState(1)
    const [measuresChangesValidation, setMeasuresChangesValidation] = useState([])
    const [validationFilterFullDay, setValidationFilterFullDay] = useLocalStorage('VALIDATE_FULL_DATE_PLUVIO', false)
    const [selectFilters, setSelectFilters] = useState({})
    const changeSelectFilters = changes => setSelectFilters({ ...selectFilters, ...changes })
    const [displayModes, setDisplayModes] = useLocalStorage('PLUVIO_VALIDATION_DISPLAY_MODES', DEFAULT_VALIDATION_DISPLAY_MODES)
    const [graphicOptions, setGraphicOptions] = useLocalStorage(PLUVIO_VALIDATION_CHART_OPTIONS, {})

    const dispatch = useDispatch()

    const townName = useMemo(() => citiesIndex[pluviometer.townCode]?.name, [citiesIndex, pluviometer])

    useTitle(() => [{
        title: i18n.pluviometry,
        href: 'pluviometry',
    }, {
        title: getStationTitleWithTownName(pluviometer, townName),
        href: `station/pluviometry/${pluviometer.id}`,
    }, {
        title: i18n.validation,
        href: `station/pluviometry/${pluviometer.id}/validationPluvio`,
    }], [pluviometer.id, townName])

    useActions(() => ({
        arrowNav: getStationArrowNav(
            'pluviometry',
            pluviometers,
            pluviometer.id,
            s => dispatch(push(`/station/pluviometry/${s.id}/validationPluvio`)),
        ),
        links: getLinks(pluviometer),
    }), [pluviometer.id, pluviometers])

    useEffect(() => {
        if (!componentHasHabilitations(H_STATION_PLUVIO_VALIDATION)) {
            dispatch(push('/unauthorized'))
        }
    }, [])

    const { isLoaded, progress } = useProgressDispatch(() => {
        const promises = !pluviometers.length ? [PluviometryAction.fetchPluviometers] : []
        const promisesDataType = !pluviometryDataTypes.length ? [...promises, PluviometryAction.fetchPluviometryDataTypes] : promises
        const promisesSettings = !applicationSettings.length ? [...promisesDataType, AdministrationAction.fetchApplicationSettings] : promisesDataType
        const promisesContribs = !contributors.length ? [...promisesSettings, ContributorAction.fetchContributors] : promisesSettings
        return promisesContribs.map(p => dispatch(p()))
    }, [])

    useEffect(() => {
        dispatch(PluviometryAction.fetchPluviometerLastMeasures([pluviometer.id]))
        dispatch(PluviometryAction.fetchPluviometerThresholds(pluviometer.id))
        dispatch(PluviometryAction.fetchPluviometerMeasuresStats(pluviometer.id))
    }, [pluviometer.id])

    // Le type de données par défaut est déterminé dès que les statistiques sont chargées.
    useUpdateEffect(() => {
        if (pluviometerStatistics.length && isNull(dataType)) {
            const newDataType = hasValue(pluviometerStatistics[0]?.typeId) ? pluviometerStatistics[0]?.typeId : 1
            setDataType(newDataType)
            setSelectedCodePoint(pluviometer.link_pointPrels?.filter(p => p.typeId === newDataType)[0]?.point)
        }
    }, [pluviometerStatistics])

    useUpdateEffect(() => {
        if (hasValue(startDate) && hasValue(endDate) && startDate > endDate && tableView) {
            setStartDate(endDate)
            setEndDate(startDate)
        }
    }, [startDate, endDate])

    useEffect(() => {
        if (isLoaded && !isNull(dataType)) {
            setDataLoaded(false)
            const group = keys(displayModes).find(mode => displayModes[mode])
            const groupFunc = group === AUTO ? getAutoCmumul(minDate - 1, maxDate) : getGroupFunc(group, displayModes)
            dispatch(PluviometryAction.loadPluvioChronicMeasuresValidation([{
                stationId: pluviometer.id,
                dataType,
                groupFunc,
                startDate: minDate,
                endDate: maxDate,
                codepoint: selectedCodePoint,
                tempRefOffsetHours: tempRefOffset,
            }])).then(() => {
                setDataLoaded(true)
            })
        }
    }, [isLoaded, dataType, time, isChange, displayModes, selectedCodePoint, minDate, maxDate, tempRefOffset])

    const pluvioMeasuresFormat = useMemo(() => pluvioMeasures[0]?.measures || [], [pluvioMeasures])

    const pluvioDataTypes = useMemo(() => pluviometryDataTypes.map(v => ({ id: v.id, label: v.label })), [pluviometryDataTypes])

    const createTechnicalEvent = (toolState) => {
        const getDates = () => `${i18n.fromDate} ${getFullDateInRef(startDate, tempRefOffset)} ${i18n.to} ${getFullDateInRef(endDate, tempRefOffset)}`
        const eventProps = (() => {
            switch (toolState.mode) {
                case PURGE: return { comment: `${i18n.purge} ${getDates()}\n`, problem: JSON.stringify({ tool }) }
                case RESET: return { comment: `${i18n.reseting} ${getDates()}\n`, problem: JSON.stringify({ tool }) }
                case DELETE: return { comment: `${i18n.deleteAction} ${getDates()}\n`, problem: JSON.stringify({ tool }) }
                case VALIDATION: return { comment: `${i18n.validation} ${getDates()}\n`, problem: JSON.stringify({ tool }) }
                case COMPENSATION: return { comment: `${i18n.compensation} ${getDates()}\n`, problem: JSON.stringify({ tool }) }
                case CORRECTION: return { comment: `${i18n.appliedCorrections} ${getDates()}\n`, problem: JSON.stringify({ tool }) }
                case IMPORT: return { comment: `${i18n.importName} ${getDates()}\n`, problem: JSON.stringify({ tool }) }
                default: return { comment: '' }
            }
        })()
        const newEvent = {
            // la passerelle pluvio ne réinjecte pas l'id de la station dans le corps, contrairement à l'hydro :
            // sans 'code' le service rejette l'évènement en 400
            code: pluviometer.id,
            eventType: 'T',
            date: moment().valueOf(),
            eventHour: moment().valueOf(),
            startDate,
            endDate,
            graph: '1',
            ...eventProps,
        }
        dispatch(EventsAction.addEvent(STATION_TYPE_NAME.pluviometry, pluviometer.id, newEvent))
    }

    const measuresSelectedByFilters = useMemo(() => {
        if ([VALIDATION, PURGE].includes(tool)) {
            const withStatus = (m) => hasValue(selectFilters.filterStatus) ? m.status === parseInt(selectFilters.filterStatus) : true
            const withQualification = (m) => hasValue(selectFilters.filterQualification) ? m.qualification === selectFilters.filterQualification : true
            const withSuperior = (m) => hasValue(selectFilters.filterSuperior) ? m.value > selectFilters.filterSuperior : true
            const withInferior = (m) => hasValue(selectFilters.filterInferior) ? m.value < selectFilters.filterInferior : true
            const withProducer = (m) => hasValue(selectFilters.filterProducer) ? m.producer === selectFilters.filterProducer : true
            const withManager = (m) => hasValue(selectFilters.filterManager) ? m.manager === selectFilters.filterManager : true
            const withValidator = (m) => hasValue(selectFilters.filterValidator) ? m.validator === selectFilters.filterValidator : true
            return pluvioMeasuresFormat.filter(m => m.date >= startDate && m.date <= endDate && withStatus(m) && withQualification(m) && withSuperior(m) && withInferior(m) && withProducer(m) && withManager(m) && withValidator(m))
        }
        return pluvioMeasuresFormat.filter(m => m.date >= startDate && m.date <= endDate)
    }, [pluvioMeasuresFormat, startDate, endDate, selectFilters, tool])

    const nextMeasure = useMemo(() => pluvioMeasuresFormat.find(p => p.date > endDate), [pluvioMeasuresFormat, endDate])

    const filteredTypesList = useMemo(() => pluviometryDataTypes
        .filter(dt => pluviometerStatistics.map(s => s.typeId).includes(dt.id))
        .map(dt => ({ id: dt.id, label: dt.label })), [pluviometerStatistics, pluviometryDataTypes])

    const minDateFilterPreview = (date) => {
        const allFirstMeasures = pluvioMeasures.flatMap(obj => obj.measures.length ? [obj.measures[0].date] : [])
        return date ?? min(allFirstMeasures) ?? minBy(pluviometerStatistics, 'startDate')?.startDate ?? moment().subtract(1, 'years').valueOf()
    }
    const { minDate: formattedMinDate, maxDate: formattedMaxDate } = getChartDate(minDateFilterPreview(minDate), maxDate || moment().valueOf(), time, tempRefOffset)

    const mode = hasValue(graphicOptions.maxY) || hasValue(graphicOptions.minY) ? 'manual' : 'auto'

    const filterFullDay = { validationFilterFullDay, setValidationFilterFullDay }

    const icons = [
        { icon: validateImg, toolIcon: VALIDATION, width: 35, tooltip: i18n.validation },
        { icon: purgeImg, toolIcon: PURGE, width: 30, tooltip: i18n.deleting },
        { icon: importImg, toolIcon: IMPORT, width: 30, tooltip: i18n.manualImport, show: !tableView },
        { icon: compensationImg, toolIcon: COMPENSATION, width: 50, tooltip: i18n.compensation, show: !tableView },
        { icon: correctionImg, toolIcon: CORRECTION, width: 45, tooltip: i18n.driftingCorrection, show: !tableView },
        { icon: optionsImg, toolIcon: OPTIONS, width: 35, tooltip: i18n.chartOptions, show: !tableView },
        { icon: thresholdImg, toolIcon: THRESHOLDS, width: 35, tooltip: i18n.thresholds },
        { icon: eventImg, toolIcon: EVENT, width: 35, tooltip: i18n.events },
    ]

    const IconMode = tableView ? FormatListBulleted : MultilineChart

    return (!isLoaded || isNull(dataType)) ? (
        <Grid container size={12} sx={{ padding: '1rem' }}>
            <Grid size={12}>
                <ProgressCard progress={progress * 0.9} round />
            </Grid>
        </Grid>
    ) : (
        <Grid container size={12} padding={1} spacing={1}>
            <Grid size={9}>
                <Card round>
                    <CardContent>
                        <Grid container alignItems='center' justifyContent='flex-start'>
                            <MUIChartTabs
                                offsetHours={tempRefOffset}
                                time={time}
                                onChangeTime={(newTime) => {
                                    setMinDate(newTime.minDate)
                                    setMaxDate(newTime.maxDate)
                                    setTime(newTime.time)
                                }}
                                stationType={STATION_TYPE_NAME.pluviometry}
                                statistics={pluviometerStatistics}
                            />
                            <Grid paddingLeft={2}>
                                <SimpleChartTabsPluvio
                                    displayModes={displayModes}
                                    changeParent={setDisplayModes}
                                    displayModesUniqChoise
                                />
                            </Grid>
                            <Grid paddingLeft={1}>
                                <ChartTempRefChip
                                    tempRef={tempRef}
                                    offsetHours={tempRefOffset}
                                    stationOffsetHours={stationTempRefOffset}
                                    stationTempRefs={pluviometer?.link_tempRefs}
                                    onChange={setTempRef}
                                />
                            </Grid>
                            <Grid
                                onClick={() => {
                                    if (isPreview) {
                                        dispatch(ToastrAction.warning(i18n.pleaseSaveModifs))
                                    } else {
                                        setTool(OPTIONS)
                                    }
                                }}
                                sx={{
                                    cursor: 'pointer',
                                    backgroundColor: 'rgba(53, 96, 159, 1)',
                                    color: 'white',
                                    padding: '0px 9px',
                                    borderTop: '2px solid rgba(53, 96, 159, 1)',
                                    borderLeft: '2px solid rgba(53, 96, 159, 1)',
                                    borderRight: '2px solid rgba(53, 96, 159, 1)',
                                    borderRadius: '5px',
                                    marginLeft: 'auto',
                                    marginRight: '20px',
                                    height: '2.7rem',
                                }}
                                container
                                justifyContent='flex-start'
                                alignItems='center'
                            >
                                {i18n.chartScale} : { mode === 'auto' ? i18n.automatic : i18n.manual }
                            </Grid>
                        </Grid>
                        {!dataLoaded ? (
                            <Grid sx={{ padding: '1rem' }}>
                                <ProgressCard indeterminate round />
                            </Grid>
                        ) : !pluvioMeasuresFormat.length ? (
                            <Grid sx={{ padding: '1rem' }}>
                                <MessageCard>{i18n.notExistDataPeriod}</MessageCard>
                            </Grid>
                        ) : tableView ? (
                            <Grid sx={{ paddingTop: '1rem' }}>
                                <TableViewValidationPluvioMode
                                    selection={selection}
                                    selectionData={selectionData}
                                    setSelectionData={setSelectionData}
                                    measuresData={pluvioMeasures}
                                    startDate={startDate}
                                    endDate={endDate}
                                    setStartDate={setStartDate}
                                    setEndDate={setEndDate}
                                    setValue={setValue}
                                    setInitialPoint={setInitialPoint}
                                />
                            </Grid>
                        ) : (
                            <GraphicValidationPluvioMode
                                tool={tool}
                                tempRef={tempRef}
                                dataLoaded={dataLoaded}
                                selection={selection}
                                setStartDate={setStartDate}
                                setEndDate={setEndDate}
                                pluvioMeasuresFormat={pluvioMeasuresFormat}
                                measuresSelectedByFilters={measuresSelectedByFilters}
                                graphicOptions={graphicOptions}
                                startDate={startDate}
                                endDate={endDate}
                                dataType={dataType}
                                pluvioDataTypes={pluvioDataTypes}
                                minDate={formattedMinDate}
                                maxDate={formattedMaxDate}
                                addLinePrevious={isPreview}
                                setAddLinePrevious={setIsPreview}
                                isPreview={isPreview}
                                isThresholShow={isThresholShow}
                                serieLinePrevious={serieLinePrevious}
                                setSerieLinePrevious={setSerieLinePrevious}
                                pluviometryThresholds={pluviometerThresholds}
                                contributors={contributors}
                                setValueCorrection={setValueCorrection}
                                measuresChangesValidation={measuresChangesValidation}
                                filterFullDay={filterFullDay}
                            />
                        )}
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={3}>
                <Card round>
                    <Grid container alignItems='center' spacing={1} sx={{ paddingTop: '10px', paddingLeft: '10px' }}>
                        <Grid size={10}>
                            <SuperMultiAutocomplete
                                values={dataType}
                                options={filteredTypesList}
                                label={i18n.dataType}
                                onChange={v => {
                                    setSelectedCodePoint(pluviometer.link_pointPrels?.find(s => s.typeId === v)?.point)
                                    setDataType(v)
                                }}
                                noNullValue
                                noSort
                            />
                        </Grid>
                        <Grid size={2}>
                            <Tooltip placement='top-start' arrow title={tableView ? i18n.graphicMode : i18n.tableMode}>
                                <IconMode
                                    onClick={() => listView.includes(tool) && setTableView(!tableView)}
                                    sx={{
                                        fontSize: '2.5rem',
                                        color: !listView.includes(tool) ? 'grey' : '#2196F3',
                                        cursor: 'pointer',
                                    }}
                                />
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <Grid container alignItems='center' spacing={1} justifyContent='left' sx={{ paddingTop: '10px', paddingLeft: '10px' }}>
                        <Grid size={10}>
                            <Select
                                value={selectedCodePoint}
                                options={pluviometer.link_pointPrels?.filter(s => s.typeId === dataType)}
                                label={i18n.pointPrelevement}
                                onChange={setSelectedCodePoint}
                                noNullValue
                                noSort
                                keyValue='point'
                            />
                        </Grid>
                    </Grid>
                </Card>
                <Card round>
                    <ul style={{ textAlign: 'center', marginTop: 5, marginBottom: 5, paddingTop: 5, paddingBottom: 5 }}>
                        {icons.filter(icon => icon.show !== false).map(icon => (
                            <IconTool
                                key={icon.toolIcon}
                                icon={icon.icon}
                                toolIcon={icon.toolIcon}
                                height={35}
                                width={icon.width}
                                tooltip={icon.tooltip}
                                setTool={(newToolIcon) => isPreview ? dispatch(ToastrAction.warning(i18n.pleaseSaveModifs)) : setTool(newToolIcon)}
                                iconSelected={tool}
                                isPreview={isPreview}
                            />
                        ))}
                    </ul>
                </Card>
                <div className='padding-top-1' />
                {tool === VALIDATION && (
                    <ValidationPluvioToolPanel
                        selection={selection}
                        setSelection={setSelection}
                        setSelectionData={setSelectionData}
                        dates={{ startDate, endDate }}
                        value={value}
                        setValue={setValue}
                        initialPoint={initialPoint}
                        setInitialPoint={setInitialPoint}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                        setIsChange={setIsChange}
                        isChange={isChange}
                        createTechnicalEvent={createTechnicalEvent}
                        setIsPreview={setIsPreview}
                        isPreview={isPreview}
                        pluvioMeasuresFormat={pluvioMeasuresFormat}
                        setMeasuresChangesValidation={setMeasuresChangesValidation}
                        measuresChangesValidation={measuresChangesValidation}
                        measuresSelectedByFilters={measuresSelectedByFilters}
                        dataType={dataType}
                        selectedCodePoint={selectedCodePoint}
                        setSelectedCodePoint={setSelectedCodePoint}
                        filterFullDay={filterFullDay}
                        displayModes={displayModes}
                        changeSelectFilters={changeSelectFilters}
                        selectFilters={selectFilters}
                    />
                )}
                {tool === PURGE && (
                    <DeletePluvioToolPanel
                        id={pluviometer.id}
                        dataType={dataType}
                        selectedCodePoint={selectedCodePoint}
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        setIsChange={setIsChange}
                        isChange={isChange}
                        isPreview={isPreview}
                        setIsPreview={setIsPreview}
                        measuresSelectedByFilters={measuresSelectedByFilters}
                        createTechnicalEvent={createTechnicalEvent}
                        displayModes={displayModes}
                        nextMeasure={nextMeasure}
                        changeSelectFilters={changeSelectFilters}
                        selectFilters={selectFilters}
                    />
                )}
                {tool === COMPENSATION && (
                    <CompensationPluvioToolPanel
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        setIsChange={setIsChange}
                        isChange={isChange}
                        createTechnicalEvent={createTechnicalEvent}
                        measuresSelectedByFilters={measuresSelectedByFilters}
                        serieLinePrevious={serieLinePrevious}
                        setSerieLinePrevious={setSerieLinePrevious}
                        isPreview={isPreview}
                        setIsPreview={setIsPreview}
                        measuresData={pluvioMeasures}
                    />
                )}
                {tool === CORRECTION && (
                    <CorrectionPluvioToolPanel
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        setIsChange={setIsChange}
                        isChange={isChange}
                        createTechnicalEvent={createTechnicalEvent}
                        measuresSelectedByFilters={measuresSelectedByFilters}
                        setSerieLinePrevious={setSerieLinePrevious}
                        isPreview={isPreview}
                        setIsPreview={setIsPreview}
                        valueCorrection={valueCorrection}
                        setValueCorrection={setValueCorrection}
                        measuresData={pluvioMeasures}
                        serieLinePrevious={serieLinePrevious}
                    />
                )}
                {tool === EVENT && (
                    <EventsToolPanelPluvio
                        pluviometer={pluviometer}
                    />
                )}
                {tool === THRESHOLDS && (
                    <>
                        <ThresholdsTab
                            thresholds={pluviometerThresholds.filter(f => parseInt(f.dataType) === dataType)}
                            station={pluviometer}
                            onReload={() => dispatch(PluviometryAction.fetchPluviometerThresholds(pluviometer.id))}
                            withoutDataTypeAnnotation
                            selectedDataType={dataType?.toString()}
                        />
                        <div style={{ marginTop: 10, backgroundColor: 'white', padding: 5 }}>
                            <Checkbox
                                label={i18n.displayThresholds}
                                checked={isThresholShow}
                                onChange={() => setIsThresholShow(!isThresholShow)}
                            />
                        </div>
                    </>
                )}
                {tool === IMPORT && (
                    <ImportPluvioToolPanel
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        applicationSettings={applicationSettings}
                        selectedDataType={dataType}
                        id={pluviometer.id}
                        pluviometer={pluviometer}
                        pluvioMeasuresFormat={pluvioMeasuresFormat}
                        isChange={isChange}
                        setIsChange={setIsChange}
                        pluvioLastMeasures={pluviometerLastMeasures[0]}
                        createTechnicalEvent={createTechnicalEvent}
                    />
                )}
                {tool === OPTIONS && (
                    <SimpleOptionsToolPanel
                        showTitle={false}
                        defaultOptions={graphicOptions}
                        updateState={newGraphicOptions => setGraphicOptions(prev => ({ ...prev, ...newGraphicOptions }))}
                    />
                )}
            </Grid>
        </Grid>
    )
}

export default PluviometryValidationApp
